import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks/redux";
import useWindowSize from "../../../../hooks/useWindowSize";
import { useDebounce } from "usehooks-ts";
import {
  fetchClients,
  setCurrentPage,
} from "../../../../store/actions/clientActions";
import Button from "../../../generic/buttons/Button/Button";
import Loading from "../../Loading/Loading";
import ModalEditClient from "../../modals/ModalEditClient/ModalEditClient";
import ModalSuccess from "../../modals/ModalSuccess/ModalSuccess";
import Pagination from "../../../generic/navigation/Pagination";
import ClientLine from "../../Lines/ClientLine/ClientLine";
import { IDataTableTitle } from "../../../../models";
import Search from "../../Search";
import { IFetchClientsParams } from "../../../../models/client";
import classes from "../Handbook.module.scss";

export default function Clients() {
  const dispatch = useAppDispatch();
  const windowSize = useWindowSize();
  const {
    list,
    isLoading,
    totalCount,
    pages,
    pageSize,
    fetchError,
    currentPage,
  } = useAppSelector((state) => state.client);
  const [modalAddShown, modalAddToggle] = useState(false);
  const [modalAddSuccessShown, modalAddSuccessToggle] = useState(false);
  const [modalEditSuccessShown, modalEditSuccessToggle] = useState(false);
  const [modalDeleteSuccessShown, modalDeleteSuccessToggle] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedSearchQuery = useDebounce<string>(query, 500);

  const pageChangeHandler = ({ selected }: { selected: number }) => {
    dispatch(setCurrentPage(selected + 1));
  };

  const changeSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const fetchParams = useMemo(() => {
    const obj: IFetchClientsParams = {
      page: currentPage,
      pageSize,
    };
    if (debouncedSearchQuery) {
      obj.query = debouncedSearchQuery;
      obj.pageSize = 0;
      obj.page = 1;
    }
    return obj;
  }, [currentPage, pageSize, debouncedSearchQuery]);

  useEffect(() => {
    dispatch(fetchClients(fetchParams));
  }, [dispatch, fetchParams]);

  const titles: IDataTableTitle[] = [
    {
      id: 1,
      name: "ФИО клиента",
    },
      /*   {
      id: 2,
      name: "Дата рождения",
    },
 {
      id: 3,
      name: "Рост (см)",
    }, */
    {
      id: 4,
      name: "Вес (кг)",
    },
    {
      id: 5,
      name: "Телефон клиента",
    },
    {
      id: 6,
      name: "",
      className: classes.data_cell_menu,
    },
  ];

  const headerFieldsTemplate = titles.map((item) => {
    const cellCls = [classes.data_cell];

    if (item.className) {
      cellCls.push(item.className);
    }

    return (
      <div className={cellCls.join(" ")} key={item.id}>
        <span>{item.name}</span>
      </div>
    );
  });

  const clientLines = useMemo(
    () =>
      list.map((item) => (
        <ClientLine
          key={item.id}
          item={item}
          editSuccess={modalEditSuccessToggle}
          deleteSuccess={modalDeleteSuccessToggle}
        />
      )),
    [list]
  );

  const dataTemplate = !isLoading && totalCount > 0 && (
    <div className={classes.data}>
      {windowSize.width >= 992 && (
        <div className={classes.data_header}>{headerFieldsTemplate}</div>
      )}
      {clientLines}
    </div>
  );

  const emptyTemplate = !isLoading && totalCount === 0 && !fetchError && (
    <div className={classes.empty}>
      <div className={classes.empty_text}>{"Клиенты не найдены"}</div>
    </div>
  );

  const errorTemplate = !isLoading && fetchError && (
    <div className={classes.error}>
      <div className={classes.error_text}>{fetchError}</div>
    </div>
  );

  const modalAdd = modalAddShown && (
    <ModalEditClient
      close={() => modalAddToggle(false)}
      editSuccess={modalAddSuccessToggle}
    />
  );

  const modalAddSuccess = modalAddSuccessShown && (
    <ModalSuccess
      close={() => modalAddSuccessToggle(false)}
      title={"Клиент добавлен!"}
    />
  );

  const modalUpdateSuccess = modalEditSuccessShown && (
    <ModalSuccess
      close={() => modalEditSuccessToggle(false)}
      title={"Клиент обновлен!"}
    />
  );

  const modalDeleteSuccess = modalDeleteSuccessShown && (
    <ModalSuccess
      headerIconColor={"red"}
      close={() => modalDeleteSuccessToggle(false)}
      title={"Клиент удален!"}
    />
  );

  return (
    <div className={classes.container}>
      {!fetchError && (
        <div className={classes.top}>
          <div className={classes.search}>
            <Search value={query} onChange={changeSearchHandler} />
          </div>
          <div className={classes.button}>
            <Button
              color={"red"}
              onClick={() => modalAddToggle(true)}
              className={classes.button_add}
            >
              Добавить клиента
            </Button>
          </div>
        </div>
      )}
      <div className={classes.content}>
        {isLoading && <Loading />}
        {dataTemplate}
        {emptyTemplate}
        {errorTemplate}
      </div>
      <div className={classes.bottom}>
        <Pagination
          onPageChange={pageChangeHandler}
          pageRangeDisplayed={5}
          pageCount={pages}
          forcePage={currentPage - 1}
        />
      </div>
      {modalAdd}
      {modalAddSuccess}
      {modalUpdateSuccess}
      {modalDeleteSuccess}
    </div>
  );
}
